// SPDX-License-Identifier: AGPL-3.0-or-later

export const SPEECH_GATE_PROCESSOR_NAME = 'lafon-speech-gate';

/**
 * AudioWorklet that passes only human speech, adding only ~5 ms latency:
 * - with a ~5 ms lookahead, energy onsets that are not clicks open the gate at reduced gain ("tentative"), Silero VAD confirms within
 *   ~1-3 frames (32 ms each) and the gate goes fully open, or closes again for clicks/breaths;
 * - after the last speech frame the gate holds open briefly so word endings are not cut;
 * - it learns the user's own speech level and ignores much quieter speech (people in the background,
 *   distant TV). The learned level adapts on its own, so near and far microphones need no setting;
 * - Silero VAD itself runs here, on the real-time audio thread, so a minimized/backgrounded app (games!)
 *   keeps exactly the same behaviour; until the weights arrive the gate is a plain pass-through.
 */
export const SPEECH_GATE_WORKLET_SOURCE = `
const FRAME = 512;
const VAD_RATE = 16000;
const OPEN_PROB = 0.5;
const KEEP_PROB = 0.3;
/** Short hold after speech: keeps word endings, but cuts the room reverb/echo tail of distant mics. */
const HANGOVER_S = 0.2;
/** Long enough to cover VAD frame fill + Silero's 1-2 frame ramp-up, so first syllables are not chopped. */
const TENTATIVE_S = 0.15;
const REFRACTORY_S = 0.12;
const TENTATIVE_GAIN = 0.5;
/** Closed gate keeps a -40 dB floor instead of digital silence: avoids Opus DTX/NetEq restarts on every word. */
const CLOSED_GAIN = 0.01;
/** Speech-only AGC: lift distant/quiet microphones toward a normal talking level, never boost noise. */
const AGC_TARGET_DB = -26;
const AGC_MAX_BOOST_DB = 16;
/** Gain rises gently (~19 dB/s) but drops almost instantly, so moving closer never blasts. */
const AGC_UP_DB_PER_BLOCK = 0.05;
const AGC_DOWN_DB_PER_BLOCK = 1.5;
const AGC_PEAK_CEILING = 0.7;
const LIMIT = 0.89;
const TRANSIENT_DROP_DB = 12;
/** ~5 ms at 48 kHz: enough to tell a click from a speech onset before it reaches the output. */
const LOOKAHEAD_BLOCKS = 2;
const ONSET_ABOVE_NOISE_DB = 12;
const MIN_ONSET_DB = -58;
const BACKGROUND_BELOW_SPEECH_DB = 18;
const SPEECH_LEVEL_MIN_FRAMES = 60;


// ---- Silero VAD v5 (16 kHz branch) evaluated in plain JS on the audio thread. -----------------------
// Output matches onnxruntime to ~1e-6. Work is split into 5 stages over consecutive render quanta (~13 ms
// decision latency, at most ~0.2 ms of work in any quantum).
class SileroVad {
  constructor(buffer) {
    const all = new Float32Array(buffer);
    let off = 0;
    const take = (n) => { const v = all.subarray(off, off + n); off += n; return v; };
    this.basis = take(258 * 256);
    this.layers = [
      {w: take(128 * 129 * 3), b: take(128), out: 128, inp: 129, stride: 1},
      {w: take(64 * 128 * 3), b: take(64), out: 64, inp: 128, stride: 2},
      {w: take(64 * 64 * 3), b: take(64), out: 64, inp: 64, stride: 2},
      {w: take(128 * 64 * 3), b: take(128), out: 128, inp: 64, stride: 1},
    ];
    this.wih = take(512 * 128);
    this.whh = take(512 * 128);
    this.bih = take(512);
    this.bhh = take(512);
    this.dw = take(128);
    this.db = take(1)[0];
    if (off !== all.length) throw new Error('bad VAD weights');
    this.h = new Float32Array(128);
    this.c = new Float32Array(128);
    this.ctx = new Float32Array(64);
    this.x = new Float32Array(640);
    this.mag = new Float32Array(129 * 4);
    this.a0 = new Float32Array(128 * 4);
    this.a1 = new Float32Array(64 * 2);
    this.a2 = new Float32Array(64);
    this.a3 = new Float32Array(128);
    this.gates = new Float32Array(512);
    this.stage = -1;
    this.seq = 0;
  }
  start(chunk, seq) {
    const x = this.x;
    x.set(this.ctx, 0);
    x.set(chunk, 64);
    for (let i = 0; i < 64; i++) x[576 + i] = x[574 - i];
    this.ctx.set(chunk.subarray(448));
    this.seq = seq;
    this.stage = 0;
  }
  stft(t) {
    const x = this.x, basis = this.basis, mag = this.mag, off = t * 128;
    for (let f = 0; f < 129; f++) {
      let re = 0, im = 0;
      const br = f * 256, bi = (f + 129) * 256;
      for (let k = 0; k < 256; k++) { const v = x[off + k]; re += basis[br + k] * v; im += basis[bi + k] * v; }
      mag[f * 4 + t] = Math.sqrt(re * re + im * im);
    }
  }
  conv(inp, T, l, out, from = 0, to = l.out) {
    const Tout = Math.floor((T - 1) / l.stride) + 1;
    for (let o = from; o < to; o++) {
      for (let t = 0; t < Tout; t++) {
        let s = l.b[o];
        const base = t * l.stride - 1;
        for (let c = 0; c < l.inp; c++) {
          const wo = (o * l.inp + c) * 3, ib = c * T;
          for (let k = 0; k < 3; k++) { const ti = base + k; if (ti >= 0 && ti < T) s += l.w[wo + k] * inp[ib + ti]; }
        }
        out[o * Tout + t] = s > 0 ? s : 0;
      }
    }
    return Tout;
  }
  /** Runs one of 5 stages (~0.2 ms max); returns the probability when finished, otherwise -1. */
  step() {
    const stage = this.stage;
    if (stage < 0) return -1;
    if (stage === 0) {
      this.stft(0);
      this.stft(1);
    } else if (stage === 1) {
      this.stft(2);
      this.stft(3);
    } else if (stage === 2) {
      this.conv(this.mag, 4, this.layers[0], this.a0);
    } else if (stage === 3) {
      const t1 = this.conv(this.a0, 4, this.layers[1], this.a1);
      const t2 = this.conv(this.a1, t1, this.layers[2], this.a2);
      this.conv(this.a2, t2, this.layers[3], this.a3);
    } else {
      const g = this.gates, a = this.a3, h = this.h, c = this.c;
      for (let r = 0; r < 512; r++) {
        let sum = this.bih[r] + this.bhh[r];
        const ro = r * 128;
        for (let k = 0; k < 128; k++) sum += this.wih[ro + k] * a[k] + this.whh[ro + k] * h[k];
        g[r] = sum;
      }
      let o = this.db;
      for (let k = 0; k < 128; k++) {
        const ig = 1 / (1 + Math.exp(-g[k]));
        const fg = 1 / (1 + Math.exp(-g[128 + k]));
        const cg = Math.tanh(g[256 + k]);
        const og = 1 / (1 + Math.exp(-g[384 + k]));
        c[k] = fg * c[k] + ig * cg;
        h[k] = og * Math.tanh(c[k]);
        if (h[k] > 0) o += this.dw[k] * h[k];
      }
      this.stage = -1;
      const p = 1 / (1 + Math.exp(-o));
      return Number.isFinite(p) ? p : 0;
    }
    this.stage = stage + 1;
    return -1;
  }
  busy() {
    return this.stage >= 0;
  }
}

function toDb(meanSquare) {
  return 10 * Math.log10(meanSquare + 1e-12);
}

class LafonSpeechGate extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ratio = sampleRate / VAD_RATE;
    this.frame = new Float32Array(FRAME);
    this.frameLen = 0;
    this.acc = 0;
    this.accCount = 0;
    this.accPos = 0;
    this.seq = 0;
    this.levels = new Float32Array(16);
    this.t = 0;
    this.vad = null;
    this.vadState = 'pending';
    this.lastProbAt = 0;
    this.confirmedUntil = 0;
    this.tentativeUntil = 0;
    this.refractoryUntil = 0;
    this.delay = null;
    this.userThresholdDb = -200;
    this.agcDb = 0;
    this.agcLin = 1;
    this.levelEnv = AGC_TARGET_DB;
    this.delayDb = null;
    this.speaking = false;
    this.noiseDb = -70;
    this.speechDb = -30;
    this.speechFrames = 0;
    this.gain = 1;
    this.attack = 1 - Math.exp(-1 / (0.003 * sampleRate));
    this.release = 1 - Math.exp(-1 / (0.05 * sampleRate));
    this.alive = true;
    this.port.onmessage = (event) => {
      const data = event.data || {};
      if (data.type === 'weights' && data.buffer) {
        try {
          this.vad = new SileroVad(data.buffer);
          this.onVad({type: 'ready'});
        } catch (_) {
          this.onVad({type: 'failed'});
        }
      } else if (data.type === 'threshold') {
        // Manual input sensitivity from settings (dBFS); null = automatic.
        this.userThresholdDb = typeof data.db === 'number' && Number.isFinite(data.db) ? data.db : -200;
      } else if (data.type === 'dispose') {
        this.alive = false;
      }
    };
  }

  onVad(data) {
    if (data.type === 'ready') {
      this.vadState = 'ready';
      this.lastProbAt = this.t;
      this.gain = 0;
      return;
    }
    if (data.type === 'failed') {
      this.vadState = 'failed';
      return;
    }
    if (data.type !== 'prob') return;
    this.lastProbAt = this.t;
    const level = this.levels[data.seq & 15];
    let p = data.p;
    if (p >= OPEN_PROB && level < this.userThresholdDb) {
      // Below the user's manual sensitivity threshold.
      p = Math.min(p, KEEP_PROB - 0.01);
      this.tentativeUntil = 0;
    } else if (p >= OPEN_PROB) {
      if (this.speechFrames >= SPEECH_LEVEL_MIN_FRAMES && level < this.speechDb - BACKGROUND_BELOW_SPEECH_DB) {
        // Speech, but far quieter than the user's own voice: someone in the background.
        p = Math.min(p, KEEP_PROB - 0.01);
        this.tentativeUntil = 0;
      } else if (p >= 0.8 && level > -60) {
        // Follow the user's own level; slower downward so a quiet sentence does not lower it, but a mic
        // moved further away is still tracked within seconds.
        const alpha = this.speechFrames < SPEECH_LEVEL_MIN_FRAMES ? 0.1 : level > this.speechDb - 10 ? 0.03 : 0.01;
        this.speechDb += (level - this.speechDb) * alpha;
        this.speechFrames++;
      }
    } else if (p < 0.15) {
      this.noiseDb += (level - this.noiseDb) * (level < this.noiseDb ? 0.2 : 0.02);
    }
    if (p < 0.15 && this.confirmedUntil <= this.t) {
      // VAD says this onset is not speech (sniff, snap, tap): drop the tentative opening right away.
      this.tentativeUntil = 0;
    }
    const hangover = HANGOVER_S * sampleRate;
    if (p >= OPEN_PROB) {
      this.confirmedUntil = this.t + hangover;
      this.tentativeUntil = 0;
    } else if (p >= KEEP_PROB && this.confirmedUntil > this.t) {
      this.confirmedUntil = Math.max(this.confirmedUntil, this.t + hangover * 0.5);
    }
    const speaking = this.confirmedUntil > this.t;
    if (speaking !== this.speaking) {
      this.speaking = speaking;
      this.port.postMessage({type: 'speaking', value: speaking});
    }
  }

  pushVadSample(sample) {
    this.acc += sample;
    this.accCount++;
    this.accPos += 1;
    if (this.accPos < this.ratio) return;
    this.accPos -= this.ratio;
    this.frame[this.frameLen++] = this.acc / this.accCount;
    this.acc = 0;
    this.accCount = 0;
    if (this.frameLen < FRAME) return;
    this.frameLen = 0;
    let sum = 0;
    for (let i = 0; i < FRAME; i++) sum += this.frame[i] * this.frame[i];
    const seq = this.seq++;
    this.levels[seq & 15] = toDb(sum / FRAME);
    const vad = this.vad;
    if (vad && this.vadState === 'ready') {
      // Normally the previous frame finished long ago; if not (low sample rates), finish it now.
      while (vad.busy()) {
        const p = vad.step();
        if (p >= 0) this.onVad({type: 'prob', seq: vad.seq, p});
      }
      vad.start(this.frame, seq);
    }
  }

  process(inputs, outputs) {
    if (!this.alive) return false;
    const input = inputs[0];
    const output = outputs[0];
    const out = output && output[0];
    if (!out) return true;
    const inp = input && input[0];
    const n = out.length;
    if (!this.delay || this.delay[0].length !== n) {
      this.delay = [];
      for (let k = 0; k <= LOOKAHEAD_BLOCKS; k++) this.delay.push(new Float32Array(n));
      this.delayDb = new Float32Array(LOOKAHEAD_BLOCKS + 1).fill(-100);
    }
    // Shift the lookahead line: delay[0] is the block being output now, delay[last] the newest input.
    const recycled = this.delay.shift();
    if (inp) recycled.set(inp); else recycled.fill(0);
    this.delay.push(recycled);
    this.delayDb.copyWithin(0, 1);
    let sum = 0;
    for (let i = 0; i < n; i++) {
      const s = recycled[i];
      sum += s * s;
      this.pushVadSample(s);
    }
    this.delayDb[LOOKAHEAD_BLOCKS] = toDb(sum / n);
    this.t += n;
    const outT = this.t - (LOOKAHEAD_BLOCKS + 1) * n;
    const current = this.delay[0];
    const blockDb = this.delayDb[0];
    let target = 1;
    if (this.vad && this.vad.busy()) {
      const p = this.vad.step();
      if (p >= 0) this.onVad({type: 'prob', seq: this.vad.seq, p});
    }
    if (this.vadState === 'ready') {
      if (this.confirmedUntil > outT) {
        target = 1;
      } else {
        const learned = this.speechFrames >= SPEECH_LEVEL_MIN_FRAMES;
        let futureMin = 0;
        for (let k = 1; k <= LOOKAHEAD_BLOCKS; k++) futureMin = Math.min(futureMin, this.delayDb[k]);
        if (
          this.tentativeUntil <= outT &&
          this.refractoryUntil <= outT &&
          blockDb > MIN_ONSET_DB &&
          blockDb > this.userThresholdDb &&
          blockDb > this.noiseDb + ONSET_ABOVE_NOISE_DB &&
          (!learned || blockDb > this.speechDb - BACKGROUND_BELOW_SPEECH_DB)
        ) {
          // Clicks and taps collapse within a few ms; speech onsets do not. The lookahead sees it first.
          if (futureMin >= blockDb - TRANSIENT_DROP_DB) {
            this.tentativeUntil = outT + TENTATIVE_S * sampleRate;
            this.refractoryUntil = this.tentativeUntil + REFRACTORY_S * sampleRate;
          }
        }
        target = this.tentativeUntil > outT ? TENTATIVE_GAIN : CLOSED_GAIN;
      }
    }
    if (this.confirmedUntil > outT && blockDb > -60 && blockDb > this.noiseDb + 15) {
      // Voiced block of the user's confirmed speech: follow its level (fast up, slow down).
      this.levelEnv += (blockDb - this.levelEnv) * (blockDb > this.levelEnv ? 0.25 : 0.012);
    }
    const wanted = Math.max(0, Math.min(AGC_MAX_BOOST_DB, AGC_TARGET_DB - this.levelEnv));
    this.agcDb = wanted < this.agcDb
      ? Math.max(wanted, this.agcDb - AGC_DOWN_DB_PER_BLOCK)
      : Math.min(wanted, this.agcDb + AGC_UP_DB_PER_BLOCK);
    // Peak guard over the output block and the lookahead: never let the boost push a peak past the ceiling.
    let peak = 0;
    for (let k = 0; k <= LOOKAHEAD_BLOCKS; k++) {
      const block = this.delay[k];
      for (let i = 0; i < n; i++) {
        const a = block[i] < 0 ? -block[i] : block[i];
        if (a > peak) peak = a;
      }
    }
    if (peak > 0) {
      const maxDb = 20 * Math.log10(AGC_PEAK_CEILING / peak);
      if (this.agcDb > maxDb) this.agcDb = Math.max(0, maxDb);
    }
    // Ramp the AGC across the block (no per-block steps: those sound like zipper/robotic artefacts).
    const agcTo = Math.pow(10, this.agcDb / 20);
    const agcFrom = this.agcLin;
    const agcStep = (agcTo - agcFrom) / n;
    this.agcLin = agcTo;
    let gain = this.gain;
    const coeff = target > gain ? this.attack : this.release;
    for (let i = 0; i < n; i++) {
      gain += (target - gain) * coeff;
      let y = current[i] * gain * (agcFrom + agcStep * (i + 1));
      const a = y < 0 ? -y : y;
      if (a > LIMIT) {
        // Soft limiter so AGC boost never clips.
        const over = (a - LIMIT) / (1 - LIMIT);
        y = (y < 0 ? -1 : 1) * (LIMIT + (1 - LIMIT) * Math.tanh(over));
      }
      out[i] = y;
    }
    this.gain = gain;
    for (let c = 1; c < output.length; c++) output[c].set(out);
    return true;
  }
}

registerProcessor('${'lafon-speech-gate'}', LafonSpeechGate);
`;
